import { Box, Flex, Image, Text } from '@chakra-ui/react';
import DownloadButtons from '@/travo/components/ui/buttons/DownloadButtons';
import { CancelWaves, Container, DisplayHeading, DotPill, PhoneFrame, Postmark, Stamp } from '@/travo/components/ui/postmark';
import { useCopy } from '@/travo/i18n';
import { HARD_SHADOW } from '@/travo/theme/constants';
import balloonPhoto from '@/travo/assets/images/home/banner.webp';
import mapScreenshot from '@/travo/assets/images/home/previews/app-home-map.webp';

/** app-home-map.webp is 640×1387; update with the image. */
const MAP_SCREENSHOT_RATIO = '640 / 1387';
import appIcon from '@/travo/assets/icons/logo-256.webp';

const HeroCopy = () => {
  const copy = useCopy();

  return (
    <Flex direction="column" gap="28px" flex="1 1 600px" minW={0}>
      <DotPill>{copy.hero.eyebrow}</DotPill>
      <DisplayHeading as="h1" fontSize={{ base: '44px', sm: '56px', lg: '60px' }} lineHeight="1.08">
        {copy.hero.titleLead}
        {copy.phraseGap}
        <Box as="span" display="block" color="stamp">
          {copy.hero.titleAccent}
        </Box>
      </DisplayHeading>
      <Text maxW="33em" fontSize={{ base: '17px', md: '19px' }} color="inkSoft" lineHeight="1.7">
        {copy.hero.body}
      </Text>
      <Flex wrap="wrap" align="center" gap="14px">
        <DownloadButtons />
        <Text fontFamily="mono" fontSize="13px" letterSpacing="0.08em" color="muted">
          {copy.download.free}
        </Text>
      </Flex>
    </Flex>
  );
};

interface PhotoStampProps {
  alt: string;
}

/** Balloon photo framed as a tilted postage stamp. */
const PhotoStamp = ({ alt }: PhotoStampProps) => (
  <Stamp position="absolute" right="2%" top="10px" w="46%" transform="rotate(6deg)" margin="18px 18px 14px" innerPadding="8px">
    <Image src={balloonPhoto} alt={alt} w="100%" h={{ base: '180px', md: '250px' }} objectFit="cover" />
    <Flex justify="space-between" align="baseline" mt="8px" fontFamily="mono" fontSize="12px" letterSpacing="0.1em">
      <Text>WINDOW SEAT</Text>
      <Text fontFamily="heading" fontSize="22px" color="stamp">
        3×
      </Text>
    </Flex>
  </Stamp>
);

interface IconStampProps {
  label: string;
}

/** App icon on a sun-colored stamp. */
const IconStamp = ({ label }: IconStampProps) => (
  <Stamp
    position="absolute"
    right="8%"
    bottom="20px"
    w={{ base: '120px', md: '150px' }}
    transform="rotate(-8deg)"
    edge="sun"
    frame="card"
    margin="14px"
    innerPadding="10px"
  >
    <Flex direction="column" align="center" gap="4px">
      <Image src={appIcon} alt="" w={{ base: '64px', md: '84px' }} aspectRatio="1" />
      <Text fontFamily="mono" fontSize="11px" letterSpacing="0.12em">
        {label}
      </Text>
    </Flex>
  </Stamp>
);

/** Collage: photo stamp, app screen, a postmark cancelling both, icon stamp. */
const HeroCollage = () => {
  const copy = useCopy();

  return (
    <Box flex="1 1 440px" minW={0} position="relative" h={{ base: '500px', md: '640px' }}>
      <PhotoStamp alt={copy.hero.photoAlt} />
      <PhoneFrame
        src={mapScreenshot}
        alt={copy.hero.phoneAlt}
        ratio={MAP_SCREENSHOT_RATIO}
        priority
        position="absolute"
        left="6%"
        top="40px"
        w="50%"
        maxW="290px"
        filter={HARD_SHADOW.phone}
      />
      <Postmark
        ring="TRAVO · TRIP STARTED · NEW TAIPEI ·"
        label="13 APR"
        sub="2026"
        position="absolute"
        left="40%"
        top={{ base: '230px', md: '300px' }}
        w={{ base: '130px', md: '170px' }}
        h={{ base: '130px', md: '170px' }}
        color="stamp"
        opacity={0.9}
        transform="rotate(-14deg)"
      />
      <CancelWaves
        position="absolute"
        left="54%"
        top={{ base: '320px', md: '420px' }}
        w={{ base: '170px', md: '240px' }}
        h={{ base: '40px', md: '56px' }}
        color="stamp"
        opacity={0.85}
        transform="rotate(-14deg)"
      />
      <IconStamp label={copy.hero.stampLabel} />
    </Box>
  );
};

const Hero = () => (
  <Container as="section" py={{ base: '40px', md: '48px' }} pb={{ base: '72px', md: '96px' }}>
    <Flex wrap="wrap" align="center" gap="56px">
      <HeroCopy />
      <HeroCollage />
    </Flex>
  </Container>
);

export default Hero;
